import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { toast } from '@livable/saas-react-components';
import { MenuItem, TextField, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from 'src/api';
import { BILLING_TYPES } from 'src/components/livy/billingType';
import { useStore } from 'src/stores';
const DERIVE = '';

/** The server's own message (a flag contradiction), else a generic one. */
const serverMessage = (err, fallback) => {
  var _response, _ref, _data$Error$GeneralEr, _data$Error, _data$error;
  const data = err === null || err === void 0 || (_response = err.response) === null || _response === void 0 ? void 0 : _response.data;
  return (_ref = (_data$Error$GeneralEr = data === null || data === void 0 || (_data$Error = data.Error) === null || _data$Error === void 0 ? void 0 : _data$Error.GeneralErrorMessage) !== null && _data$Error$GeneralEr !== void 0 ? _data$Error$GeneralEr : data === null || data === void 0 || (_data$error = data.error) === null || _data$error === void 0 ? void 0 : _data$error.generalErrorMessage) !== null && _ref !== void 0 ? _ref : fallback;
};

/**
 * Staff-only: the company's customer type and any per-property override. Saved on change; the API
 * rejects a type that contradicts the AppFolio or convergent flags, and that message is shown inline.
 */
export const CustomerTypeSection = observer(_ref2 => {
  var _company$customerType;
  let companyId = _ref2.companyId;
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const store = useStore();
  const _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    properties = _useState2[0],
    setProperties = _useState2[1];
  const _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    company = _useState4[0],
    setCompany = _useState4[1];
  const _useState5 = useState(),
    _useState6 = _slicedToArray(_useState5, 2),
    error = _useState6[0],
    setError = _useState6[1];
  const loadCompany = useCallback(() => api.Companies.getCustomerType(companyId).then(type => {
    setCompany(type);
    // Livy reads the shared store; without this its reports and help keep the old type until a reload.
    store.company.companyFeatureStore.setCustomerType(companyId, type.customerType, type.customerTypeSource);
  }).catch(() => setCompany(undefined)), [companyId, store]);
  useEffect(() => {
    void loadCompany();
    api.Companies.getPropertyCustomerTypes(companyId).then(resp => {
      var _resp$response;
      return setProperties((_resp$response = resp.response) !== null && _resp$response !== void 0 ? _resp$response : []);
    }).catch(() => setProperties([]));
  }, [companyId, loadCompany]);
  const save = async (write, after) => {
    setError(undefined);
    try {
      await write();
      after();
      toast('success', {
        body: [t('company_details.customer_type.saved')]
      });
    } catch (err) {
      setError(serverMessage(err, t('company_details.customer_type.error')));
    }
  };
  const options = emptyLabel => [/*#__PURE__*/React.createElement(MenuItem, {
    key: "derive",
    value: DERIVE
  }, emptyLabel), ...BILLING_TYPES.map(type => /*#__PURE__*/React.createElement(MenuItem, {
    key: type,
    value: type
  }, t("company_details.customer_type.types.".concat(type))))];
  const companyValue = (company === null || company === void 0 ? void 0 : company.customerTypeSource) === 'set' ? (_company$customerType = company === null || company === void 0 ? void 0 : company.customerType) !== null && _company$customerType !== void 0 ? _company$customerType : DERIVE : DERIVE;
  return /*#__PURE__*/React.createElement("div", {
    'custom-attribute': 'company-details-customer-type'
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, t('company_details.customer_type.label')), /*#__PURE__*/React.createElement(Typography, {
    variant: "body3",
    component: "div"
  }, t('company_details.customer_type.explanation')), /*#__PURE__*/React.createElement(TextField, {
    select: true,
    size: "small",
    label: t('company_details.customer_type.label'),
    value: companyValue,
    helperText: (company === null || company === void 0 ? void 0 : company.customerTypeSource) === 'derived' && company !== null && company !== void 0 && company.customerType ? "".concat(t('company_details.customer_type.derive'), ": ").concat(t("company_details.customer_type.types.".concat(company === null || company === void 0 ? void 0 : company.customerType))) : undefined,
    onChange: event => {
      const value = event.target.value || null;
      void save(() => api.Companies.setCustomerType(companyId, value), loadCompany);
    },
    sx: {
      minWidth: 260,
      mt: 1
    }
  }, options(t('company_details.customer_type.derive'))), properties.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    sx: {
      mt: 2
    }
  }, t('company_details.customer_type.properties')), properties.map(property => {
    var _property$customerTyp;
    return /*#__PURE__*/React.createElement(TextField, {
      key: property.propertyId,
      select: true,
      size: "small",
      label: property.propertyAddress || property.propertyId,
      value: (_property$customerTyp = property.customerTypeOverride) !== null && _property$customerTyp !== void 0 ? _property$customerTyp : DERIVE,
      onChange: event => {
        const value = event.target.value || null;
        void save(() => api.Companies.setPropertyCustomerType(property.propertyId, value), () => setProperties(list => list.map(p => p.propertyId === property.propertyId ? _objectSpread(_objectSpread({}, p), {}, {
          customerTypeOverride: value
        }) : p)));
      },
      sx: {
        minWidth: 260,
        mt: 1,
        display: 'flex'
      }
    }, options(t('company_details.customer_type.follow_company')));
  })), error && /*#__PURE__*/React.createElement(Typography, {
    role: "alert",
    color: "error",
    variant: "body3",
    component: "div",
    sx: {
      mt: 1
    }
  }, error));
});